import type { TemplateResponse } from 'services/types/message';

import baseWorkloadApi from './baseWorkloadApi';

// Supports both positional (string) and named params (object with paramName and value)
export type BodyComponentInput = string | { paramName: string; value: string };

export interface WhatsAppCampaign {
  id: number;
  businessUnitId: number;
  businessUnit?: {
    id: number;
    name: string;
  };
  name: string;
  state: 'RUNNING' | 'COMPLETED' | 'CANCELLED';
  messagesToSendPerPeriod: number;
  timePeriodInMinutes: number;
  startDate?: string;
  startHour?: number;
  endHour?: number;
  sendOnWeekend?: boolean;
  templateComponents?: { bodyComponents: BodyComponentInput[] };
  templateName?: string;
  fileUrl?: string;
  uploadedFileName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignData {
  businessUnitId: number | null;
  selectedTemplate: TemplateResponse[number] | null;
  templateBodyParameters: BodyComponentInput[];
  campaignName: string;
  messagesToSendPerPeriod: number;
  timePeriodInMinutes: number;
  startDate: Date | null;
  startHour: number | null;
  endHour: number | null;
  sendOnWeekend: boolean;
  totalFileRows: number | null;
  uploadedFile: File | null;
  mappedHeaders: { headerName: string; attrCliente: string }[] | null;
  separator: string | null;
}

const adminCampaignsApi = baseWorkloadApi.injectEndpoints({
  endpoints: (build) => ({
    uploadWhatsAppCampaignClients: build.mutation({
      query: (props: {
        businessUnitId: number;
        campaignName: string;
        templateName: string;
        mappedHeaders: { headerName: string; attrCliente: string }[];
        separator: string;
        messagesToSendPerPeriod: number;
        timePeriodInMinutes: number;
        startDate?: Date;
        startHour?: number;
        endHour?: number;
        sendOnWeekend?: boolean;
        templateComponents: { bodyComponents: BodyComponentInput[] };
        file: File;
      }) => {
        const formData = new FormData();
        formData.append('file', props.file);
        formData.append('businessUnitId', props.businessUnitId.toString());
        formData.append('campaignName', props.campaignName);
        formData.append('templateName', props.templateName);
        formData.append('mappedHeaders', JSON.stringify(props.mappedHeaders));
        formData.append('separator', props.separator);
        formData.append('messagesToSendPerPeriod', props.messagesToSendPerPeriod.toString());
        formData.append('timePeriodInMinutes', props.timePeriodInMinutes.toString());
        formData.append('templateComponents', JSON.stringify(props.templateComponents));
        if (props.startDate) {
          formData.append('startDate', props.startDate.toISOString());
        }
        if (props.startHour !== undefined) {
          formData.append('startHour', props.startHour.toString());
        }
        if (props.endHour !== undefined) {
          formData.append('endHour', props.endHour.toString());
        }
        if (props.sendOnWeekend !== undefined) {
          formData.append('sendOnWeekend', props.sendOnWeekend.toString());
        }
        return {
          url: '/campaigns/upload-whatsapp-campaign-clients',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: {
        createdClients: number[];
        updatedClients: number[];
        failedClients: { phone: string; error: string }[];
        skippedClients: { row: number; phone: string; reason: string }[];
        parseErrors: { row: number; code: string; message: string }[];
        createdCars: number;
        updatedCars: number;
        skippedCars: { row: number; identifier: string; reason: string }[];
        failedCars: { row: number; identifier: string; error: string }[];
        aborted: boolean;
        WhatsAppCampaign: {
          id: number;
          businessUnitId: number;
          name: string;
          state: string;
          messagesToSendPerPeriod: number;
          timePeriodInMinutes: number;
        };
        scheduledMessages: unknown[];
      }) => response,
    }),
    mapCSVHeaders: build.mutation({
      query: (props: { headers: string[]; exampleData: string[][] }) => ({
        url: '/campaigns/map-csv-headers',
        method: 'POST',
        body: props,
      }),
      transformResponse: (response: { mappedHeaders: { headerName: string; attrCliente: string }[] }) => response,
    }),

    getMessagesSchedule: build.mutation({
      query: (props: {
        messagesToSendPerPeriod: number;
        totalClients: number;
        timePeriodInMinutes: number;
        campaignStartTime?: Date;
        startHour?: number;
        endHour?: number;
        sendOnWeekend?: boolean;
      }) => ({
        url: '/campaigns/get-messages-schedule',
        method: 'POST',
        body: props,
      }),
      transformResponse: (response: Record<string, number>) => response,
    }),
    getAllWhatsAppCampaigns: build.query({
      query: () => ({
        url: '/campaigns/get-all-whatsapp-campaigns',
        method: 'GET',
      }),
      transformResponse: (response: { WhatsAppCampaigns: WhatsAppCampaign[] }) => response,
    }),

    getWhatsAppCampaign: build.query({
      query: (campaignId: number) => ({
        url: `/campaigns/get-whatsapp-campaign/${campaignId}`,
        method: 'GET',
      }),
      transformResponse: (response: { WhatsAppCampaign: WhatsAppCampaign; messagesByStatus: Record<string, number> }) =>
        response,
    }),

    cancelWhatsAppCampaign: build.mutation({
      query: (campaignId: number) => ({
        url: '/campaigns/cancel-whatsapp-campaign',
        method: 'POST',
        body: { whatsAppCampaignId: campaignId },
      }),
      transformResponse: (response: { success: boolean }) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useUploadWhatsAppCampaignClientsMutation,
  useMapCSVHeadersMutation,
  useGetMessagesScheduleMutation,
  useGetAllWhatsAppCampaignsQuery,
  useGetWhatsAppCampaignQuery,
  useCancelWhatsAppCampaignMutation,
} = adminCampaignsApi;
